import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import SimplePageHero from '../marketing/components/SimplePageHero';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const icon = (path: React.ReactNode) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    {path}
  </svg>
);

const cards = [
  { title: 'Blog', desc: 'Compliance, tax, legal and accounting — 50+ articles for UK landlords.', cta: 'Read the blog', to: '/blog', bg: C.accent, icon: icon(<><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>) },
  { title: 'Guides', desc: 'Seven definitive long-form pillar guides on compliance, tax, HMOs and more.', cta: 'Browse guides', to: '/guides', bg: C.ink, icon: icon(<><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></>) },
  { title: 'Calculators', desc: 'Ten instant tools: yield, stamp duty, ROI, tax and portfolio health.', cta: 'Open calculators', to: '/calculators', bg: C.accent, icon: icon(<><rect x="4" y="2" width="16" height="20" rx="2" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="8" y1="14" x2="8" y2="18" /></>) },
  { title: 'Templates', desc: 'Downloadable checklists and spreadsheets in PDF, XLSX and DOCX.', cta: 'Get templates', to: '/templates', bg: C.ink, icon: icon(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>) },
  { title: 'Checklists', desc: 'Step-by-step compliance checklists for every stage of a tenancy.', cta: 'View checklists', to: '/checklists', bg: C.accent, icon: icon(<><path d="m9 11 3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>) },
  { title: 'Case Studies', desc: 'Real UK landlords and agents sharing how they run on MillOS.', cta: 'Read case studies', to: '/case-studies', bg: C.ink, icon: icon(<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></>) },
  { title: 'Help / FAQ', desc: 'Answers to the most common questions about MillOS and compliance.', cta: 'Get help', to: '/help', bg: C.accent, icon: icon(<><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12" y2="17" /></>) },
  { title: 'News', desc: "Product updates, integrations and UK property-law changes.", cta: "See what's new", to: '/news', bg: C.ink, icon: icon(<><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" /><path d="M18 14h-8" /><path d="M15 18h-5" /><path d="M10 6h8v4h-8z" /></>) },
  { title: 'Compliance & Accounting Centres', desc: 'The two product pillars, each with its own deep-dive landing page.', cta: 'Explore the pillars', to: '/compliance-centre', bg: C.accent, icon: icon(<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></>) },
];

const ResourcesPage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'Resources for UK Landlords — Guides, Calculators & Templates | MillOS',
    description: 'Everything UK landlords need in one hub: the blog, 7 pillar guides, 10 calculators, downloadable templates, checklists, case studies and help.',
    canonicalPath: '/resources',
  });

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <SimplePageHero
        eyebrow="Resource hub"
        title={<>Everything for the <Box component="em" sx={{ fontStyle: 'italic' }}>modern landlord</Box></>}
        body="Guides, calculators, templates and playbooks — free, practical and written for UK landlords."
      />
      <Box component="section" sx={{ padding: { xs: '48px 16px 64px', md: '56px 52px 96px' } }}>
        <Box sx={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3,1fr)' }, gap: '22px' }}>
          {cards.map((c) => (
            <Box
              key={c.title}
              component={RouterLink}
              to={c.to}
              sx={{ display: 'flex', flexDirection: 'column', gap: '16px', background: C.panel, borderRadius: '24px', padding: '30px', height: '100%', '&:hover': { background: C.tintBlue } }}
            >
              <Box sx={{ width: 50, height: 50, borderRadius: '14px', background: c.bg, display: 'grid', placeItems: 'center' }}>{c.icon}</Box>
              <Box>
                <Box component="h2" sx={{ fontFamily: F.display, fontSize: 26, lineHeight: 1.1, margin: 0 }}>{c.title}</Box>
                <Box sx={{ marginTop: '10px', color: C.body, fontSize: 14.5, lineHeight: 1.55 }}>{c.desc}</Box>
              </Box>
              <Box sx={{ marginTop: 'auto', fontSize: 14, fontWeight: 500, color: C.accent }}>{c.cta} →</Box>
            </Box>
          ))}
        </Box>
      </Box>
      <MarketingFooter />
    </Box>
  );
};

export default ResourcesPage;
